import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { graphql } from "gatsby";
import React from "react";
import { twJoin } from "tailwind-merge";

import RichText from "../richText";
import TransitionHeight from "../transitionHeight";

type Props = {
  slice:
    | Queries.ContentFAQSliceFragment
    | Queries.DentistFAQSliceFragment
    | Queries.LocationFAQSliceFragment
    | Queries.ProductFAQSliceFragment
    | Queries.TreatmentFAQSliceFragment
    | Queries.TreatmentAddonFAQSliceFragment
    | Queries.MarketingTreatmentFAQSliceFragment;
};

const Faq: React.FC<Props> = ({ slice }) => {
  const { primary, items } = slice;
  const { title, bgGrey, noBorder } = primary;

  if (items.length < 1 || !items[0].faq_question || !items[0].faq_question.document) return null;

  return (
    <section
      className={twJoin(
        "p-t-xxxl-fluid p-b-xxxl-fluid mx-auto max-w-screen-max",
        bgGrey ? "bg-neutral-50" : "bg-white",
        !noBorder && "border-b border-b-black/10",
      )}
    >
      <section className="col-span-full gap-0 mx-5 max-w-screen-xl md:mx-11 lg:mx-24 xl:mx-auto baseGrid lg:gap-x-lg">
        <div className="grid col-span-full lg:col-start-5 lg:col-span-16">
          <div className="mb-2 typo-h2">{title?.text}</div>
          <ul className="pl-0 list-none">
            {items.map((item, index) => {
              const document = { data: null, ...item.faq_question?.document };
              if (!document.data) return null;

              const question = document.data;

              return (
                <Disclosure
                  key={index}
                  as="li"
                  className="pl-0 border-b p-t-md-fluid p-b-md-fluid animated-list-item border-b-black/10"
                >
                  {({ open }: { open: boolean }) => (
                    <>
                      <DisclosureButton className="flex justify-between items-center w-full text-left typo-h5">
                        {question.title?.text}
                        <div
                          className={twJoin(
                            "animated-toggle-button ml-2 h-[15px] w-[15px] min-w-[15px]",
                            open && "open",
                          )}
                        />
                      </DisclosureButton>
                      <TransitionHeight height={open ? "auto" : 0}>
                        <DisclosurePanel static as="div" className="m-t-sm-fluid">
                          {question.body.map((item, index) => {
                            const slice = { primary: null, ...item };
                            if (!slice.primary?.text) return null;

                            return (
                              <RichText
                                key={index}
                                data={slice.primary.text.richText}
                                className="block typo-base text-black/70"
                              />
                            );
                          })}
                        </DisclosurePanel>
                      </TransitionHeight>
                    </>
                  )}
                </Disclosure>
              );
            })}
          </ul>
        </div>
      </section>
    </section>
  );
};

export const query = graphql`
  fragment DentistFAQSlice on PrismicPageDentistDataBodyFaq {
    ...Slice

    primary {
      title: faq_title {
        text
      }
      bgGrey: faq_background_color
      noBorder: faq_hide_border
    }
    items {
      faq_question {
        document {
          ... on PrismicFaqQuestion {
            data {
              title {
                text
              }
              body {
                ...Slice
                ... on PrismicFaqQuestionDataBodyText {
                  primary {
                    text {
                      ...RichText
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  fragment ContentFAQSlice on PrismicPageContentDataBodyFaq {
    ...Slice

    primary {
      title: faq_title {
        text
      }
      bgGrey: faq_background_color
      noBorder: faq_hide_border
    }
    items {
      faq_question {
        document {
          ... on PrismicFaqQuestion {
            data {
              title {
                text
              }
              body {
                ...Slice
                ... on PrismicFaqQuestionDataBodyText {
                  primary {
                    text {
                      ...RichText
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  fragment LocationFAQSlice on PrismicLocationDataBodyFaq {
    ...Slice

    primary {
      title: faq_title {
        text
      }
      bgGrey: faq_background_color
      noBorder: faq_hide_border
    }
    items {
      faq_question {
        document {
          ... on PrismicFaqQuestion {
            data {
              title {
                text
              }
              body {
                ...Slice
                ... on PrismicFaqQuestionDataBodyText {
                  primary {
                    text {
                      ...RichText
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  fragment ProductFAQSlice on PrismicProductDataBodyFaq {
    ...Slice

    primary {
      title: faq_title {
        text
      }
      bgGrey: faq_background_color
      noBorder: faq_hide_border
    }
    items {
      faq_question {
        document {
          ... on PrismicFaqQuestion {
            data {
              title {
                text
              }
              body {
                ...Slice
                ... on PrismicFaqQuestionDataBodyText {
                  primary {
                    text {
                      ...RichText
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  fragment TreatmentFAQSlice on PrismicTreatmentDataBodyFaq {
    ...Slice

    primary {
      title: faq_title {
        text
      }
      bgGrey: faq_background_color
      noBorder: faq_hide_border
    }
    items {
      faq_question {
        document {
          ... on PrismicFaqQuestion {
            data {
              title {
                text
              }
              body {
                ...Slice
                ... on PrismicFaqQuestionDataBodyText {
                  primary {
                    text {
                      ...RichText
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  fragment TreatmentAddonFAQSlice on PrismicTreatmentAddonDataBodyFaq {
    ...Slice

    primary {
      title: faq_title {
        text
      }
      bgGrey: faq_background_color
      noBorder: faq_hide_border
    }
    items {
      faq_question {
        document {
          ... on PrismicFaqQuestion {
            data {
              title {
                text
              }
              body {
                ...Slice
                ... on PrismicFaqQuestionDataBodyText {
                  primary {
                    text {
                      ...RichText
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  fragment MarketingTreatmentFAQSlice on PrismicMarketingTreatmentDataBodyFaq {
    ...Slice

    primary {
      title: faq_title {
        text
      }
      bgGrey: faq_background_color
      noBorder: faq_hide_border
    }
    items {
      faq_question {
        document {
          ... on PrismicFaqQuestion {
            data {
              title {
                text
              }
              body {
                ...Slice
                ... on PrismicFaqQuestionDataBodyText {
                  primary {
                    text {
                      ...RichText
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
export default Faq;
